/**
 * Corner Cabinet utilities (Feb 2026)
 * -------------------------------------------------------------
 * Corner Cabinet is now a container — it has NO fixed height. Its
 * height is derived at runtime from the sum of its internal section
 * heights, exactly like a Multi-Column container.
 *
 * Data model (persisted on the single corner ITEM):
 *   cornerItems: [ { id, type, side, settings } ]
 *
 *   ─── PER-TYPE settings.shape ──────────────────────────────
 *   shelves           { count, eachLevelHeight }          → sectionHeight = count * eachLevelHeight
 *   single-shelf      { height }                          → sectionHeight = height
 *   shoe-shelves      { count, height }                   → sectionHeight = height
 *   hanging           { hangingType, rodSide, height,
 *                       hasDivider, dividerCount }        → sectionHeight = height
 *   horizontal-filler { height }                          → sectionHeight = height
 *   mirror            { height }                          → sectionHeight = height
 *
 * `item.height` on the corner is a MIRROR of the derived height. Any
 * code that mutates `cornerItems` MUST re-run `computeCornerHeight`
 * and update `item.height` in the same store transaction so 2D + 3D
 * stay in sync.
 */

import { HANGING_TYPES } from './catalog';

// Types allowed inside a Corner Cabinet.
export const CORNER_ALLOWED_TYPES = [
  { key: 'shelves',           label: 'Shelves',           defaults: { count: 4, eachLevelHeight: 12 } },
  { key: 'single-shelf',      label: 'Single Shelf',      defaults: { height: 12 } },
  { key: 'shoe-shelves',      label: 'Shoe Shelves',      defaults: { count: 4, height: 36 } },
  { key: 'hanging',           label: 'Hanging',           defaults: { hangingType: HANGING_TYPES.SINGLE, rodSide: 'first', hasDivider: false, height: 50 } },
  { key: 'horizontal-filler', label: 'Horizontal Filler', defaults: { height: 2 } },
  { key: 'mirror',            label: 'Mirror',            defaults: { height: 30 } },
];

// Types explicitly blocked from being placed inside a Corner Cabinet.
export const CORNER_BLOCKED_TYPES = new Set([
  'drawers', 'sliding-shelves', 'sliding-shelf', 'sliding-filler',
  'vertical-filler', 'tv-niche', 'pant-rack', 'hook-rack',
]);

// Return the effective vertical height (INCHES) of a single corner section.
// This is the ONLY place height-per-type is derived — every caller uses this.
export function sectionHeight(cornerItem) {
  if (!cornerItem) return 0;
  const s = cornerItem.settings || {};
  const t = cornerItem.type;
  if (t === 'shelves') {
    const n = Math.max(1, Number(s.count) || 1);
    const each = Math.max(0, Number(s.eachLevelHeight) || 0);
    return n * each;
  }
  // All other allowed types persist their own vertical span in `height`.
  return Math.max(0, Number(s.height) || 0);
}

// Sum of every internal section height. Used for the Corner's total
// height on both 2D and 3D — Corner has NO extra padding, its height
// is EXACTLY the stack height (top/bottom L-boards are inside the sum).
export function computeCornerHeight(cornerItems) {
  if (!Array.isArray(cornerItems) || cornerItems.length === 0) return 0;
  return cornerItems.reduce((tot, it) => tot + sectionHeight(it), 0);
}
